/**
 * Pure helpers over `StorageResourceRef` for the browser's navigation model:
 * tab titles, breadcrumb segments, parent / child refs and the root
 * containment rule (navigation never leaves the tab's root, spec §6.1).
 */
import type { StorageItem, StorageResourceRef } from '../../../../types/azureStorage';
import { parentDirectoryRef, pathSegments } from '../../../../core/engines/azureStorageFilePaths';
import { fileTabTitle, shareLabel } from '../files/fileTabTitle';

type FileLocation = Extract<StorageResourceRef, { kind: 'share' | 'directory' | 'file' }>;
const isFileLocation = (ref: StorageResourceRef): ref is FileLocation => ref.kind === 'share' || ref.kind === 'directory' || ref.kind === 'file';
/** The folder path a file-share location stands for ('' = the share root). */
const folderPathOf = (ref: FileLocation): string => (ref.kind === 'share' ? '' : pathSegments(ref.path).join('/'));
const underFolder = (path: string, folder: string): boolean => folder === '' || path === folder || path.startsWith(`${folder}/`);
import { hnsBreadcrumbSegments, hnsLocationInRoot, hnsParentRef, isWithinHnsRoot } from '../hns/hnsPaths';

export function lastPathSegment(path: string): string {
  const trimmed = path.endsWith('/') ? path.slice(0, -1) : path;
  const index = trimmed.lastIndexOf('/');
  return index === -1 ? trimmed : trimmed.slice(index + 1);
}

/** `<container>/<last prefix segment>` or `<table>` / `<queue>` / `<share>/<dir>` (spec §6.1; a snapshot `<share>@<time>`). */
export function storageTabTitle(ref: StorageResourceRef): string {
  switch (ref.kind) {
    case 'account':
      return 'Storage account';
    case 'service':
      return ref.service;
    case 'container':
      return ref.container;
    case 'blobPrefix':
      return ref.prefix ? `${ref.container}/${lastPathSegment(ref.prefix)}` : ref.container;
    case 'blob':
      return `${ref.container}/${lastPathSegment(ref.name)}`;
    case 'table':
    case 'entity':
      return ref.table;
    case 'queue':
    case 'message':
      return ref.queue;
    case 'share':
    case 'directory':
    case 'file':
      return fileTabTitle(ref); // S7: `share@<local time>` for a snapshot
    case 'hnsPath':
      return ref.path ? `${ref.fileSystem}/${lastPathSegment(ref.path)}` : ref.fileSystem;
  }
}

/** Slash-joined path for "Copy path" and tooltips (no account, no SAS). */
export function storageResourcePath(ref: StorageResourceRef): string {
  switch (ref.kind) {
    case 'account':
      return '';
    case 'service':
      return ref.service;
    case 'container':
      return ref.container;
    case 'blobPrefix':
      return `${ref.container}/${ref.prefix}`;
    case 'blob':
      return `${ref.container}/${ref.name}`;
    case 'table':
      return ref.table;
    case 'entity':
      return `${ref.table}/${ref.partitionKey}/${ref.rowKey}`;
    case 'queue':
      return ref.queue;
    case 'message':
      return `${ref.queue}/${ref.messageId}`;
    case 'share':
      return ref.share;
    case 'directory':
    case 'file':
      return ref.path ? `${ref.share}/${ref.path}` : ref.share;
    case 'hnsPath':
      return ref.path ? `${ref.fileSystem}/${ref.path}` : ref.fileSystem;
  }
}

/** `inert` (UI/UX fix round A, U36): a folder of a single attached file / blob — shown, never navigated (outside the attachment). */
export type StorageBreadcrumbSegment = { label: string; ref: StorageResourceRef; inert?: true };

/** A single attached item's path as its folders (inert) and its own name (the root). */
function singleItemSegments(path: string, root: StorageResourceRef): StorageBreadcrumbSegment[] {
  const parts = path.split('/').filter(Boolean);
  const name = parts.pop() ?? path;
  return [...parts.map((part): StorageBreadcrumbSegment => ({ label: part, ref: root, inert: true })), { label: name, ref: root }];
}

/** Root first, then one segment per prefix level of the current location (blob mode only in S1); a single-blob root reads `container › folder › name` (U36). */
export function storageBreadcrumbSegments(root: StorageResourceRef, resource: StorageResourceRef): StorageBreadcrumbSegment[] {
  if (root.kind === 'hnsPath') return hnsBreadcrumbSegments(root, resource); // S8
  if (root.kind === 'blob') return [{ label: root.container, ref: { kind: 'container', container: root.container } }, ...singleItemSegments(root.name, root)];
  if (isFileLocation(root)) return fileBreadcrumbSegments(root, resource);
  if (root.kind !== 'container' && root.kind !== 'blobPrefix') return [{ label: storageTabTitle(root), ref: root }];
  const rootPrefix = root.kind === 'blobPrefix' ? root.prefix : '';
  const segments: StorageBreadcrumbSegment[] = [{ label: rootPrefix ? lastPathSegment(rootPrefix) : root.container, ref: root }];
  if (resource.kind !== 'blobPrefix' || resource.container !== root.container || !resource.prefix.startsWith(rootPrefix)) return segments;
  const relative = resource.prefix.slice(rootPrefix.length);
  let cumulative = rootPrefix;
  for (const part of relative.split('/').filter(Boolean)) {
    cumulative = `${cumulative}${part}/`;
    segments.push({ label: part, ref: { kind: 'blobPrefix', container: root.container, prefix: cumulative } });
  }
  return segments;
}

/** S7: the root (a share / folder label; a single file reads `share › folder › name`, U36), then one segment per folder below it. */
function fileBreadcrumbSegments(root: FileLocation, resource: StorageResourceRef): StorageBreadcrumbSegment[] {
  const snapshot = root.snapshot ? { snapshot: root.snapshot } : {};
  if (root.kind === 'file') return [{ label: shareLabel(root.share, root.snapshot), ref: { kind: 'share', share: root.share, ...snapshot } }, ...singleItemSegments(root.path, root)];
  const rootPath = folderPathOf(root);
  const segments: StorageBreadcrumbSegment[] = [{ label: rootPath ? lastPathSegment(rootPath) : shareLabel(root.share, root.snapshot), ref: root }];
  if (!isWithinStorageRoot(root, resource) || resource.kind !== 'directory') return segments;
  const below = pathSegments(pathSegments(resource.path).join('/').slice(rootPath.length));
  let cumulative = rootPath;
  for (const part of below) {
    cumulative = cumulative ? `${cumulative}/${part}` : part;
    segments.push({ label: part, ref: { kind: 'directory', share: root.share, path: cumulative, ...snapshot } });
  }
  return segments;
}

/** S7: same share, same snapshot identity (a live tab never shows a snapshot, a snapshot tab never the live share), path containment. */
function isWithinFileRoot(root: FileLocation, resource: StorageResourceRef): boolean {
  if (!isFileLocation(resource) || resource.share !== root.share || (resource.snapshot ?? '') !== (root.snapshot ?? '')) return false;
  if (root.kind === 'file') return resource.kind === 'file' && resource.path === root.path;
  return underFolder(folderPathOf(resource), folderPathOf(root)) && (resource.kind !== 'share' || root.kind === 'share' || folderPathOf(root) === '');
}

/** True when `resource` is the root or lives under it (same container, prefix extends the root's; same share and snapshot, path under the folder). */
export function isWithinStorageRoot(root: StorageResourceRef, resource: StorageResourceRef): boolean {
  if (isFileLocation(root)) return isWithinFileRoot(root, resource);
  if (root.kind === 'hnsPath') return isWithinHnsRoot(root, resource); // S8
  if (root.kind === 'container') {
    if (resource.kind === 'container') return resource.container === root.container;
    return resource.kind === 'blobPrefix' && resource.container === root.container;
  }
  if (root.kind === 'blobPrefix') {
    return resource.kind === 'blobPrefix' && resource.container === root.container && resource.prefix.startsWith(root.prefix);
  }
  return JSON.stringify(root) === JSON.stringify(resource);
}

/** The location one level up, or `null` at the root (Backspace / "up" are no-ops there). */
export function storageParentRef(root: StorageResourceRef, resource: StorageResourceRef): StorageResourceRef | null {
  if (isFileLocation(root)) {
    // S7: the parent folder (snapshot kept), the share root from a top-level folder — never above the tab root.
    if (resource.kind !== 'directory' || !isWithinFileRoot(root, resource) || folderPathOf(resource) === folderPathOf(root)) return null;
    const parent = parentDirectoryRef(resource);
    if (!parent || parent.path.length <= folderPathOf(root).length) return root;
    return parent;
  }
  if (root.kind === 'hnsPath') return hnsParentRef(root, resource); // S8
  if (resource.kind !== 'blobPrefix') return null;
  const rootPrefix = root.kind === 'blobPrefix' ? root.prefix : '';
  if (resource.prefix === rootPrefix || resource.prefix === '') return null;
  const trimmed = resource.prefix.slice(0, -1);
  const index = trimmed.lastIndexOf('/');
  const parentPrefix = index === -1 ? '' : trimmed.slice(0, index + 1);
  if (parentPrefix.length <= rootPrefix.length) return root.kind === 'blobPrefix' ? root : { kind: 'container', container: resource.container };
  return { kind: 'blobPrefix', container: resource.container, prefix: parentPrefix };
}

/** S8: where a folder row navigates — its Blob ref, or the equivalent Data Lake directory under an `hnsPath` root. */
export function locationInRoot(root: StorageResourceRef, ref: StorageResourceRef | null): StorageResourceRef | null {
  if (!ref || root.kind !== 'hnsPath') return ref;
  return hnsLocationInRoot(root, ref);
}

/** The ref a listing item identifies (folder rows navigate to it; blob rows download it). */
export function storageItemRef(item: StorageItem): StorageResourceRef | null {
  switch (item.kind) {
    case 'container':
      return { kind: 'container', container: item.name };
    case 'folder':
      return { kind: 'blobPrefix', container: item.container, prefix: item.prefix };
    case 'blob':
      return {
        kind: 'blob',
        container: item.container,
        name: item.name,
        ...(item.snapshot ? { snapshot: item.snapshot } : {}),
        // A versioning-enabled account stamps EVERY listed blob with the
        // versionId of its current version (Azure, verified 2026-09-19 on the
        // real account; Azurite and the fake never do) — the current version
        // IS the blob, so its ref stays plain (row key, Copy URL, download);
        // only a historical version keeps its qualifier.
        ...(item.versionId && !item.isCurrentVersion ? { versionId: item.versionId } : {}),
      };
    case 'table':
      return { kind: 'table', table: item.name };
    case 'queue':
      return { kind: 'queue', queue: item.name };
    case 'share':
      return { kind: 'share', share: item.name, ...(item.snapshot ? { snapshot: item.snapshot } : {}) };
    // S7-06: a snapshot's rows keep the snapshot — navigation, download, Copy URL and row keys all carry it.
    case 'directory':
      return { kind: 'directory', share: item.share, path: item.path, ...(item.snapshot ? { snapshot: item.snapshot } : {}) };
    case 'file':
      return { kind: 'file', share: item.share, path: item.path, ...(item.snapshot ? { snapshot: item.snapshot } : {}) };
  }
}
